import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Calendar, Image as ImageIcon, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY = { 0: "Mon", 1: "Tue", 2: "Wed", 3: "Thu", 4: "Fri", 5: "Sat", 6: "Sun" };
const NTH = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th", 5: "5th" };
const jsDayToOurDay = (js) => (js + 6) % 7;

const RECUR_LABEL = (ev) => {
  if (ev.recurrence === "monthly_nth" && ev.recurrence_nth) {
    const d = jsDayToOurDay(new Date(ev.first_date + "T00:00:00").getDay());
    return `Every ${NTH[ev.recurrence_nth]} ${DAY[d]}`;
  }
  if (ev.recurrence === "every_week") {
    const d = jsDayToOurDay(new Date(ev.first_date + "T00:00:00").getDay());
    return `Every ${DAY[d]}`;
  }
  return null;
};

const fmtDate = (d) => {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  } catch {
    return d;
  }
};

const priceFrom = (pricing) => {
  const vals = Object.values(pricing || {}).filter((v) => typeof v === "number" && v > 0);
  if (!vals.length) return null;
  return Math.min(...vals);
};

/**
 * Embeddable events calendar iframe.
 * URL: /embed/v/:slug?theme=dark|light&accent=D9A6F5&view=grid|list&limit=6&bg=transparent
 */
export default function EmbedCalendar() {
  const { slug } = useParams();
  const [params] = useSearchParams();

  const theme = params.get("theme") === "light" ? "light" : "dark";
  const accent = "#" + (params.get("accent") || "D9A6F5").replace(/^#/, "");
  const view = params.get("view") === "list" ? "list" : "grid";
  const limit = Math.max(1, Math.min(24, parseInt(params.get("limit") || "6", 10) || 6));
  const bgParam = params.get("bg");
  const transparent = bgParam === "transparent";

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/venues/${slug}`);
        setData(res.data);
      } catch (e) {
        setErr(e?.response?.status === 404 ? "Venue not found." : "Unable to load events.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const t = useMemo(() => {
    if (theme === "light") {
      return {
        bg: transparent ? "transparent" : "#FFFFFF",
        card: "#FFFFFF",
        cardBorder: "#EAEAEA",
        text: "#0A0A0A",
        muted: "#6B6B6B",
        subtle: "#F5F5F5",
      };
    }
    return {
      bg: transparent ? "transparent" : "#0A0A0A",
      card: "#111111",
      cardBorder: "rgba(255,255,255,0.10)",
      text: "#F5F5F0",
      muted: "#A3A3A3",
      subtle: "rgba(255,255,255,0.04)",
    };
  }, [theme, transparent]);

  const events = (data?.upcoming_events || []).slice(0, limit);
  const publicBase = `${window.location.origin}/v/${slug}`;

  return (
    <div
      data-testid="embed-calendar-page"
      style={{
        background: t.bg,
        color: t.text,
        minHeight: "100vh",
        fontFamily:
          "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 16px" }}>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 0", color: t.muted }}>
            <Loader2 size={18} className="animate-spin" style={{ color: accent }} />
          </div>
        ) : err ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: t.muted }}>{err}</div>
        ) : events.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: t.muted }}>
            No upcoming events yet.
          </div>
        ) : view === "list" ? (
          <ul data-testid="embed-events-list" style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
            {events.map((ev) => (
              <EventListRow key={ev.id} ev={ev} t={t} accent={accent} publicBase={publicBase} />
            ))}
          </ul>
        ) : (
          <div
            data-testid="embed-events-grid"
            style={{
              display: "grid",
              gap: 14,
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            }}
          >
            {events.map((ev) => (
              <EventCard key={ev.id} ev={ev} t={t} accent={accent} publicBase={publicBase} />
            ))}
          </div>
        )}

        <div
          style={{
            marginTop: 20,
            paddingTop: 12,
            borderTop: `1px solid ${t.cardBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: t.muted,
          }}
        >
          <span>{data?.name ? `${data.name} — What's on` : "What's on"}</span>
          <a
            href="https://www.swingleforvenues.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: t.muted, textDecoration: "none" }}
          >
            Powered by <span style={{ color: accent }}>Swingle</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function EventCard({ ev, t, accent, publicBase }) {
  const recur = RECUR_LABEL(ev);
  const from = priceFrom(ev.pricing);
  return (
    <a
      href={`${publicBase}/${ev.slug}`}
      target="_top"
      rel="noopener noreferrer"
      data-testid={`embed-event-card-${ev.slug}`}
      style={{
        display: "block",
        background: t.card,
        border: `1px solid ${t.cardBorder}`,
        borderRadius: 0,
        overflow: "hidden",
        textDecoration: "none",
        color: t.text,
        transition: "border-color 200ms ease, transform 200ms ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = accent)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = t.cardBorder)}
    >
      <div
        style={{
          aspectRatio: "16 / 10",
          background: t.subtle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          borderBottom: `1px solid ${t.cardBorder}`,
        }}
      >
        {ev.photo_data_uri ? (
          <img src={ev.photo_data_uri} alt={ev.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <ImageIcon size={24} color={t.muted} />
        )}
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, color: accent, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase" }}>
          <Calendar size={11} />
          {fmtDate(ev.first_date)} · {ev.time}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.15, marginBottom: 6 }}>
          {ev.name}
        </div>
        {ev.theme && (
          <div style={{ fontSize: 12, color: t.muted, marginBottom: 8 }}>{ev.theme}</div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, fontSize: 12, color: t.muted }}>
          <span>{recur || "One-off"}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: t.text, fontWeight: 700 }}>
            {from != null ? `From £${from.toFixed(2)}` : "Details"} <ArrowRight size={12} color={accent} />
          </span>
        </div>
      </div>
    </a>
  );
}

function EventListRow({ ev, t, accent, publicBase }) {
  const recur = RECUR_LABEL(ev);
  const from = priceFrom(ev.pricing);
  return (
    <li>
      <a
        href={`${publicBase}/${ev.slug}`}
        target="_top"
        rel="noopener noreferrer"
        data-testid={`embed-event-row-${ev.slug}`}
        style={{
          display: "grid",
          gridTemplateColumns: "72px 1fr auto",
          gap: 14,
          alignItems: "center",
          padding: 12,
          background: t.card,
          border: `1px solid ${t.cardBorder}`,
          textDecoration: "none",
          color: t.text,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = accent)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = t.cardBorder)}
      >
        <div
          style={{
            width: 72,
            height: 72,
            background: t.subtle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {ev.photo_data_uri ? (
            <img src={ev.photo_data_uri} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Calendar size={18} color={accent} />
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: accent, marginBottom: 4 }}>
            {fmtDate(ev.first_date)} · {ev.time}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {ev.name}
          </div>
          <div style={{ fontSize: 12, color: t.muted }}>{ev.theme || recur || "One-off"}</div>
        </div>
        <div style={{ textAlign: "right", fontSize: 12, color: t.muted, whiteSpace: "nowrap" }}>
          <div style={{ color: t.text, fontWeight: 700, marginBottom: 2 }}>
            {from != null ? `From £${from.toFixed(2)}` : "Details"}
          </div>
          <ArrowRight size={12} color={accent} style={{ display: "inline-block" }} />
        </div>
      </a>
    </li>
  );
}
